// Renaming a document is TWO mutations that must land together: the file
// rename, and the `[[ref]]` rewrites in every document pointing at it.
//
// They used to be applied in that order from the pane — edits first, rename
// second — which was harmless while `renameFile` always succeeded. It no longer
// does: it refuses a target that is taken, is a directory, or lives under a
// file. A refused rename then left every inbound reference rewritten to a
// document that was never created.
//
// So the rename goes FIRST and the edits only follow if it landed. Lifting this
// out of `DocumentsPane` also keeps that file a pure view component.

import { computeDocRename } from "../workbench/primitives/md-editor/renameDoc";
import type { WorkspaceStore } from "../workbench/WorkspaceStore";

const stemOf = (name: string): string => name.replace(/\.(md|markdown)$/i, "");

/**
 * Rename the document at `from` to `to`, carrying its inbound `[[refs]]`.
 * Returns false when the workspace refused the rename — nothing is changed.
 */
export function renameDocFile(store: WorkspaceStore, from: string, to: string): boolean {
  if (from === to) return true;
  const files = store.getState().files;
  // A stem shared by another document is AMBIGUOUS: `[[notes]]` in teamB refers
  // to teamB's own notes, and rewriting by bare stem across the workspace
  // pointed it at teamA's rename. Rename the file, leave the refs — a ref that
  // was already ambiguous must not be silently re-aimed.
  if (stemIsAmbiguous(files, from)) return store.renameFile(from, to);
  const plan = computeDocRename(files, stemOf(basename(from)), stemOf(basename(to)), from);
  // A doc with a usable `title:` IS named by that title — the filename is only
  // where it lives — so a FILE rename must touch neither the title nor any
  // `[[ref]]`. That question has exactly ONE answer: whether `computeDocRename`
  // produced a file rename. Asking it a second way here (`parseFrontmatter`)
  // disagreed with `rewriteTitle` in both directions: a BLANK `title:` was
  // "untitled" to one and "titled" to the other, so the doc got both mutations
  // and the greedy title regex ate the following frontmatter line; an INDENTED
  // `title:` was a title to one and unrewritable to the other, so the doc's
  // refs were left dangling.
  if (!plan.rename) return store.renameFile(from, to);
  // The REQUESTED target wins over the plan's: `computeDocRename` derives its
  // own from the SOURCE file's extension, so `.md` → `.markdown` came back as
  // `.md` and the caller was told a path that does not exist had been created.
  const rename = { from, to };
  if (!store.renameFile(rename.from, rename.to)) return false;
  for (const [path, content] of plan.edits) {
    // An edit targeting the renamed file itself must follow it to its new key,
    // or it silently lands on a path that no longer exists.
    store.edit(path === rename.from ? rename.to : path, content);
  }
  return true;
}

function basename(path: string): string {
  return path.slice(path.lastIndexOf("/") + 1);
}

/** Does another document share this one's stem? Then `[[stem]]` names no single
 *  file and must not be rewritten. */
function stemIsAmbiguous(files: ReadonlyMap<string, string>, path: string): boolean {
  const stem = stemOf(basename(path)).toLowerCase();
  for (const key of files.keys()) {
    if (key === path || !/\.(md|markdown)$/i.test(key)) continue;
    if (stemOf(basename(key)).toLowerCase() === stem) return true;
  }
  return false;
}
